import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { SubscribePageActions, UiStateActions } from "./actions";
import { Observable, catchError, map, of, switchMap, tap } from "rxjs";
import { Action } from "@ngrx/store";
import { ROUTE_NAMES } from "../app-routing.module";

import { SubscriptionService } from "../@api/client-api";
import { CookieService } from 'ngx-cookie-service';
import { ProofOfWorkService } from '../services/proof-of-work.service';

@Injectable()
export class RootStoreEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private cookieService: CookieService,
    private proofOfWorkService: ProofOfWorkService
  ) {}

  postSubscription$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SubscribePageActions.subscribeInitiated),
      switchMap((action) => {
        // Run Proof Of Work challenge
        const nonce = this.cookieService.get('XSRF-TOKEN');
        const email = action.subscriptionRequest.subscriberDetails?.email ?? '';
        const signature = this.proofOfWorkService.runProofOfWorkChallenge(nonce, email);

        // Create a new subscriptionRequest object with the updated signature
        const updatedSubscriptionRequest = {
          ...action.subscriptionRequest,
          signature: signature
        };

        return this.subscriptionService
          .apiSubscriptionPost(updatedSubscriptionRequest)
          .pipe(
            map(() => {
              return SubscribePageActions.subscribeSucceeded({
                subscriptionRequest: updatedSubscriptionRequest,
              });
            }),
            tap(() => this.router.navigate([ROUTE_NAMES.CONFIRMATION])),
            catchError((e) => {
              const actions = [
                UiStateActions.setServerError({ serverError: e.error, serverErrorEmail: email }),
                e.error === 'invalid-email'
                  ? SubscribePageActions.subscribeFailedWrongEmail()
                  : SubscribePageActions.subscribeFailed(),
              ];
              return of(...actions);
            })
          );
      })
    );
  });

  subscribeFailed$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SubscribePageActions.subscribeFailed),
        tap(() => this.router.navigate([ROUTE_NAMES.ERROR]))
      );
    },
    { dispatch: false }
  );

  subscribeFailedWrongEmail$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SubscribePageActions.subscribeFailedWrongEmail),
        tap(() => {
          console.info("Invalid email");
        })
      );
    },
    { dispatch: false }
  );

  setLoading$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SubscribePageActions.subscribeInitiated,
        SubscribePageActions.subscribeSucceeded,
        SubscribePageActions.subscribeFailed,
        SubscribePageActions.subscribeFailedWrongEmail
      ),
      map((action) => {
        if (action.type === SubscribePageActions.subscribeInitiated.type) {
          return UiStateActions.setLoading({ isLoading: true });
        } else {
          return UiStateActions.setLoading({ isLoading: false });
        }
      })
    )
  });
}
